/**
 * Unit Utility Functions
 *
 * The one place a unit becomes text. What a figure means decides its unit, never the screen it
 * sits on:
 *
 * - A **stock** figure is a whole count of base units — `formatStock` → "5,000 g".
 * - A **price** is per pricing unit — `formatPerUnit` → "€12.00 / kg", and "€0.60" for a count.
 * - A **document line** reads in the unit it was entered in — `formatQuantity` → "2.5 kg".
 *
 * A unit reads as its catalogue symbol, with a space before it. A count reads as a word that
 * agrees with the number: "1 item", "240 items".
 *
 * Every function takes a named-parameter object, as `moneyUtil` does, so a call site says which
 * unit it means.
 */

import type {
  CanHoldEveryBaseUnitParams,
  ConvertQuantityParams,
  DisplayUnit,
  FormatPerUnitParams,
  FormatProgressParams,
  FormatQuantityParams,
  FormatStockParams,
  FormatStockProgressParams,
  FormatTotalParams,
  FromBaseParams,
  IsSharedUnitParams,
  PerUnitSuffixParams,
  QuantityInputParams,
  QuantityInputProps,
  StockEntry,
  StockTotalParams,
  ToBaseCountParams,
  UnitLabelParams,
  WithPerUnitParams
} from '@/utils/unit-util/UnitUtil.types'

import { COUNTING_MEASUREMENT_TYPES } from '@/domains/measurement-units/types/consts/MeasurementType.consts'
import { ITEM_UNIT_IDENTIFIER } from '@/domains/measurement-units/types/consts/MeasurementUnitIdentifier.consts'
import { format } from '@/utils/money-util/moneyUtil'

/** Decimals a figure keeps when no unit says otherwise. */
const DEFAULT_PRECISION = 3

/** The count unit's singular. Its identifier, `ITEM_UNIT_IDENTIFIER`, is already the plural. */
const SINGULAR_COUNT = 'item'

/** Any quantity other than one reads a count in the plural. */
const PLURAL_QUANTITY = 2

/**
 * How far a converted figure may sit from a whole base count, as a share of that count. Floating-point
 * error grows with the figure, so a fixed gap refuses 16,384.005 kg. At the largest stock figure,
 * 2,147,483,647, this still allows only 0.002 of a base unit, so half a gram is never taken as whole.
 */
const RELATIVE_WHOLE_TOLERANCE = 1e-12

const formatters = new Map<number, Intl.NumberFormat>()

/**
 * `Intl.NumberFormat` with no locale takes the merchant's own, so the thousands separator is the
 * one they expect. One formatter per precision, built once.
 */
const formatterFor = (precision: number): Intl.NumberFormat => {
  const cached = formatters.get(precision)
  if (cached) return cached

  const formatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: precision })
  formatters.set(precision, formatter)

  return formatter
}

const isCounting = (unit: DisplayUnit): boolean => COUNTING_MEASUREMENT_TYPES.includes(unit.type_id)

const precisionOf = (unit?: DisplayUnit): number => unit?.precision ?? DEFAULT_PRECISION

const figure = (quantity: number, unit?: DisplayUnit): string => formatterFor(precisionOf(unit)).format(quantity)

/**
 * What a unit reads as beside a quantity — `kg`, `ml`, `1 item`, `6 items`.
 *
 * ⚠ **The count unit's symbol is singular and its identifier is plural.** The seeder writes it as
 * symbol `item`, identifier `items`. A count therefore reads by its identifier, singularised at
 * one. Nothing here pluralises a symbol: that would turn `kg` into `kgs`.
 */
const unitLabel = ({ unit, quantity }: UnitLabelParams): string => {
  if (!isCounting(unit)) return unit.symbol || unit.name || unit.unit_identifier

  if (unit.unit_identifier === ITEM_UNIT_IDENTIFIER && Math.abs(quantity) === 1) return SINGULAR_COUNT

  return unit.unit_identifier
}

/** A figure in its own unit, at that unit's precision — "2.5 kg", "240 items", "1,250". */
const formatQuantity = ({ quantity, unit }: FormatQuantityParams): string => {
  const text = figure(quantity, unit)

  return unit ? `${text} ${unitLabel({ unit, quantity })}` : text
}

/** A base count in the unit it reads in; null when that unit never converts. No unit leaves it as is. */
const stockQuantity = ({ baseCount, unit }: FormatStockParams): number | null =>
  unit ? fromBase({ baseCount, unit }) : baseCount

/**
 * A stock figure — a whole base count — read in `unit`: 850 g in kg reads "0.85 kg". The base unit
 * itself has factor 1, so a base count in its base unit reads unchanged. With no unit, a bare figure.
 */
const formatStock = ({ baseCount, unit }: FormatStockParams): string => {
  const quantity = stockQuantity({ baseCount, unit })

  return quantity === null ? figure(baseCount) : formatQuantity({ quantity, unit })
}

/**
 * The number `formatStock` prints, without its label — 850 g in kg is "0.85". A unit that never
 * converts reads as the bare base count, as `formatStock` does.
 */
const formatStockFigure = ({ baseCount, unit }: FormatStockParams): string => {
  const quantity = stockQuantity({ baseCount, unit })

  return quantity === null ? figure(baseCount) : figure(quantity, unit)
}

/**
 * The label `formatStock` puts after a stock figure — "kg", "1 item", "240 items" — for a screen that sets the
 * figure apart from its unit. Undefined with no unit, or one that never converts, where `formatStock` prints a
 * bare figure.
 */
const stockUnitLabel = ({ baseCount, unit }: FormatStockParams): string | undefined => {
  if (!unit) return undefined

  const quantity = stockQuantity({ baseCount, unit })

  return quantity === null ? undefined : unitLabel({ unit, quantity })
}

/**
 * Two stock figures — whole base counts — read in `unit`, the unit stated once: 3,000 of 10,000 g in kg
 * reads "3 of 10 kg". `formatProgress` is for figures already in their unit, such as a document line.
 */
const formatStockProgress = ({ done, total, unit }: FormatStockProgressParams): string =>
  `${formatStockFigure({ baseCount: done, unit })} of ${formatStock({ baseCount: total, unit })}`

/** Received against ordered, the unit stated once — "1.5 of 2.5 kg". */
const formatProgress = ({ done, total, unit }: FormatProgressParams): string =>
  `${figure(done, unit)} of ${formatQuantity({ quantity: total, unit })}`

/**
 * What a price is per — "/ kg" — or an empty string for a count, where "per item" is noise on a
 * retail catalogue in which nearly everything is sold per item.
 */
const perUnitSuffix = ({ unit }: PerUnitSuffixParams): string => {
  if (!unit || isCounting(unit)) return ''

  return `/ ${unitLabel({ unit, quantity: 1 })}`
}

/**
 * Any text with what it is per, last — "Cost Price (Exc. VAT) / kg", "€1.2000 / kg" — and the text
 * alone for a count. Labels and pre-formatted amounts both end this way.
 */
const withPerUnit = ({ text, unit }: WithPerUnitParams): string => {
  const suffix = perUnitSuffix({ unit })

  return suffix ? `${text} ${suffix}` : text
}

/** A price with what it is per — "€12.00 / kg", "€0.60". */
const formatPerUnit = ({ money, unit }: FormatPerUnitParams): string => withPerUnit({ text: format({ money }), unit })

/**
 * The suffix a price input draws inside itself — " / kg" — or none for a count. The leading space
 * is the input's, which sets the suffix flush against the figure.
 */
const priceInputSuffix = ({ unit }: PerUnitSuffixParams): string | undefined => {
  const suffix = perUnitSuffix({ unit })

  return suffix ? ` ${suffix}` : undefined
}

/**
 * The suffix a quantity input draws inside itself — " g", " items". Read in the plural, because the
 * input does not know the figure the merchant is about to type.
 */
const quantityInputSuffix = ({ unit }: PerUnitSuffixParams): string | undefined =>
  unit ? ` ${unitLabel({ unit, quantity: PLURAL_QUANTITY })}` : undefined

/**
 * A base count expressed in `unit` — 2500 → 2.5 kg.
 *
 * Null for a packaging unit, whose factor is null because it never converts.
 */
const fromBase = ({ baseCount, unit }: FromBaseParams): number | null => {
  if (unit.conversion_factor === null) return null

  return baseCount / unit.conversion_factor
}

/**
 * A quantity restated in another unit of the same kind — 100 g → 0.1 kg.
 *
 * Null when the two measure different things, or when either is a packaging unit, which never converts.
 */
const convertQuantity = ({ quantity, from, to }: ConvertQuantityParams): number | null => {
  if (from.type_id !== to.type_id) return null
  if (from.conversion_factor === null || to.conversion_factor === null) return null

  return (quantity * from.conversion_factor) / to.conversion_factor
}

/**
 * True when every unit is the same one — the test a total must pass before it may add figures up.
 * An empty set shares trivially, and so does a set where no unit has resolved yet.
 */
const isSharedUnit = ({ units }: IsSharedUnitParams): boolean => {
  const [first] = units

  return units.every(unit => unit?.unit_identifier === first?.unit_identifier)
}

/**
 * A total over lines — "7.5 kg" when every line shares a unit, otherwise "3 lines".
 *
 * Adding grams to items gives a number that means nothing, so a mixed set is counted, not summed.
 *
 * Its twin is `formatStockTotal`, for base counts. Same shape, different fact, so they stay apart.
 */
const formatTotal = ({ entries }: FormatTotalParams): string => {
  const [first] = entries

  if (!isSharedUnit({ units: entries.map(entry => entry.unit) })) return `${entries.length} lines`

  const quantity = entries.reduce((total, entry) => total + entry.quantity, 0)

  return formatQuantity({ quantity, unit: first?.unit })
}

/**
 * True when `unit` can show every whole base count exactly: its factor exists and divides 10 ^ precision.
 * kg (1,000 at 3 decimals) can; a metre (100,000 at 3) and a packaging unit cannot.
 */
const canHoldEveryBaseUnit = ({ unit }: CanHoldEveryBaseUnitParams): boolean =>
  unit.conversion_factor !== null && 10 ** unit.precision % unit.conversion_factor === 0

/**
 * A quantity typed in `unit` as a whole base count — 4.82 kg → 4,820. Null when it is not a whole number
 * of base units (0.0005 kg), or when the unit never converts. No unit means the figure is a base count.
 */
const toBaseCount = ({ quantity, unit }: ToBaseCountParams): number | null => {
  const factor = unit ? unit.conversion_factor : 1

  if (factor === null) return null

  const baseCount = quantity * factor
  const whole = Math.round(baseCount)
  const tolerance = RELATIVE_WHOLE_TOLERANCE * Math.max(1, Math.abs(whole))

  return Math.abs(baseCount - whole) <= tolerance ? whole : null
}

/**
 * The sum of stock figures — whole base counts — with the one unit they all read in. Null when the units
 * differ, since grams added to items mean nothing. With no entry, or with no unit resolved yet, the unit is
 * undefined and the figure reads bare.
 *
 * The one place base counts are added. `formatStockTotal` prints it, and a caller that sets the figure apart
 * from its unit reads the pair.
 */
const stockTotal = ({ entries }: StockTotalParams): StockEntry | null => {
  const [first] = entries

  if (!isSharedUnit({ units: entries.map(entry => entry.unit) })) return null

  return { baseCount: entries.reduce((total, entry) => total + entry.baseCount, 0), unit: first?.unit }
}

/**
 * A stock total over lines — "7.5 kg" when every line shares a stock unit, otherwise "N lines".
 *
 * Its twin is `formatTotal`, for figures already in their unit. Same shape, different fact, so they stay apart.
 */
const formatStockTotal = ({ entries }: StockTotalParams): string => {
  const total = stockTotal({ entries })

  return total === null ? `${entries.length} lines` : formatStock(total)
}

/**
 * What a quantity input accepts in `unit` — whole numbers for grams, three decimals for a
 * kilogram, and never less than the smallest step the unit can hold.
 */
const quantityInput = ({ unit }: QuantityInputParams): QuantityInputProps => {
  const precision = unit?.precision ?? 0

  return {
    minFractionDigits: 0,
    maxFractionDigits: precision,
    min: 10 ** -precision
  }
}

export {
  convertQuantity,
  formatPerUnit,
  formatProgress,
  formatQuantity,
  formatStock,
  formatStockFigure,
  formatStockProgress,
  formatStockTotal,
  formatTotal,
  fromBase,
  canHoldEveryBaseUnit,
  isSharedUnit,
  perUnitSuffix,
  priceInputSuffix,
  quantityInput,
  quantityInputSuffix,
  stockQuantity,
  stockTotal,
  stockUnitLabel,
  toBaseCount,
  unitLabel,
  withPerUnit
}
